import type { ReactNode } from 'react';

import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';

import { useMount } from '@simplywallst/ui-core';

import { OneColumn } from '@/components/Layout';
import DocumentHeader from '@/components/DocumentHeader';

import { CompanyNoteModalProvider } from '@/components/Note';

import { DEMO_PORTFOLIO_ID } from '@/constants';
import { usePortfolio } from '@/hooks/portfolio/usePortfolio';
import type { ComponentPropsWithoutRef } from 'react';
import {
  ROUTE_PORTFOLIO_DEMO_RETURNS,
  ROUTE_PORTFOLIO_DEMO_HOLDINGS,
  ROUTE_PORTFOLIO_DEMO_NARRATIVES,
  ROUTE_PORTFOLIO_DEMO_ANALYSIS,
  ROUTE_PORTFOLIO_DEMO_DIVIDENDS,
  ROUTE_PORTFOLIO_DEMO_UPDATES,
} from '@/constants/routes';
import { useReduxStateForPortfolio } from '../../hooks/useReduxStateForPortfolio';
import { getMetadata } from './utils';

const ErrorState = loadable(() => import('../../components/ErrorState'), {
  resolveComponent: (c) => c.ErrorState,
});

const PopulatedState = loadable(() => import('./components/PopulatedState'), {
  resolveComponent: (c) => c.PopulatedState,
});

const locationTypeMap: Record<
  string,
  ComponentPropsWithoutRef<typeof PopulatedState>['initialPanel']
> = {
  [ROUTE_PORTFOLIO_DEMO_RETURNS]: 'returns',
  [ROUTE_PORTFOLIO_DEMO_HOLDINGS]: 'holdings',
  [ROUTE_PORTFOLIO_DEMO_NARRATIVES]: 'narratives',
  [ROUTE_PORTFOLIO_DEMO_ANALYSIS]: 'analysis',
  [ROUTE_PORTFOLIO_DEMO_DIVIDENDS]: 'dividend',
  [ROUTE_PORTFOLIO_DEMO_UPDATES]: 'updates',
};

const Wrapper = ({ children }: { children: ReactNode }) => {
  const { Track } = useTracking({
    type: 'track',
    subject: 'page',
  });
  const { locationType } = useReduxStateForPortfolio();
  const { title, description } = getMetadata(locationTypeMap[locationType]);
  return (
    <Track>
      <DocumentHeader title={title} description={description} allowCrawlers />
      {children}
    </Track>
  );
};

export const DemoPortfolio = () => {
  const { locationType } = useReduxStateForPortfolio();
  const { status, data: portfolio } = usePortfolio({
    id: DEMO_PORTFOLIO_ID,
  });

  const { trackEvent } = useTracking();

  const initialPanel = locationTypeMap[locationType] ?? 'holdings';

  const holdings = portfolio?.holdings ?? [];

  const isEmpty = status === 'success' && portfolio && holdings.length === 0;
  const isError =
    status === 'error' || (status === 'success' && !portfolio) || isEmpty;
  const isPopulated = status !== 'error' && portfolio && holdings.length > 0;

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  if (isError) {
    return (
      <Wrapper>
        <OneColumn>
          <ErrorState />
        </OneColumn>
      </Wrapper>
    );
  }

  if (isPopulated) {
    return (
      <Wrapper>
        <CompanyNoteModalProvider>
          <PopulatedState portfolio={portfolio} initialPanel={initialPanel} />
        </CompanyNoteModalProvider>
      </Wrapper>
    );
  }

  return null;
};
